import * as React from 'react'

import { MinusIcon, PlusIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { isMacOs } from 'react-device-detect'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import StruxhubInput, {
  IStruxhubInputProps,
} from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Keys from '~/client/src/shared/enums/Keys'

import './StruxhubNumberWithControlsInput.scss'

interface IProps extends IStruxhubInputProps {
  stepMultiplier?: number
  step: number
  min: number
  max: number
  onValueChange: (value: number) => void
}

const DEFAULT_STEP_MULTIPLIER = 10
const clickForCoarser = 'click for coarser'
const clickForFiner = 'click for finer'

@observer
export default class StruxhubNumberWithControlsInput extends React.Component<IProps> {
  public render() {
    return (
      <>
        <StruxhubInput
          {...this.props}
          customRightIcon={this.renderCustomRightIcon()}
        />
      </>
    )
  }

  private renderCustomRightIcon = () => {
    return (
      <Tooltip>
        <TooltipTrigger
          render={<div className="number-input-group row x-end" />}
        >
          <div
            className="input-icon remove pointer tw:flex tw:items-center tw:justify-center"
            onClick={this.onRemoveButtonClick}
          >
            <MinusIcon size={16} className="tw:text-foreground" />
          </div>
          <div
            className="input-icon add pointer tw:flex tw:items-center tw:justify-center"
            onClick={this.onAddButtonClick}
          >
            <PlusIcon size={16} className="tw:text-foreground" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {this.renderTooltipContent()}
        </TooltipContent>
      </Tooltip>
    )
  }

  private renderTooltipContent = () => {
    const ctrlButtonText = isMacOs ? 'CMD ⌘' : Keys.Ctrl
    return (
      <div className="col">
        <div className="row">
          <div className="brada4 no-grow pa3 tw:border tw:border-solid tw:border-muted-foreground">
            {Keys.Shift}
          </div>
          <div>
            -{clickForCoarser} {this.props.label.toLowerCase()}
          </div>
        </div>
        <div className="row">
          <div className="brada4 no-grow pa3 no-white-space-wrap tw:border tw:border-solid tw:border-muted-foreground">
            {ctrlButtonText}
          </div>
          <div>
            -{clickForFiner} {this.props.label.toLowerCase()}
          </div>
        </div>
      </div>
    )
  }

  private onRemoveButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    this.props.onValueChange(
      this.getValidatedValue(
        +this.props.value - this.props.step * this.getMultiplier(e),
      ),
    )
    e.preventDefault()
  }

  private onAddButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    this.props.onValueChange(
      this.getValidatedValue(
        +this.props.value + this.props.step * this.getMultiplier(e),
      ),
    )
    e.preventDefault()
  }

  private getValidatedValue = (value: number) => {
    const { min, max } = this.props
    if (value > max) {
      return max
    }
    if (value < min) {
      return min
    }
    return value
  }

  private getMultiplier(e: React.MouseEvent<HTMLElement>): number {
    const isCtrlPressed = e.ctrlKey || (isMacOs && e.metaKey)
    if (isCtrlPressed) {
      return 1 / (this.props.stepMultiplier || DEFAULT_STEP_MULTIPLIER)
    }
    if (e.shiftKey) {
      return this.props.stepMultiplier || DEFAULT_STEP_MULTIPLIER
    }

    return 1
  }
}
